import { useState } from "react";
import type { AgentWorkbenchAction } from "../../../../shared/types";
import { Textarea } from "../ui/Textarea.tsx";
import { Card } from "../ui/Card.tsx";
import { Button } from "../ui/Button.tsx";

export interface WorkbenchComposerRequest {
  target_section_id?: string | null;
  quick_action_id?: string | null;
  instruction?: string | null;
}

export function WorkbenchComposer({
  actions,
  selectedSectionId,
  selectedSectionLabel,
  error,
  onRequest,
  disabled = false,
  pending = false,
}: {
  actions: AgentWorkbenchAction[];
  selectedSectionId: string | null;
  selectedSectionLabel?: string | null;
  error?: string | null;
  onRequest?: (request: WorkbenchComposerRequest) => Promise<boolean> | boolean;
  disabled?: boolean;
  pending?: boolean;
}) {
  const [instruction, setInstruction] = useState("");
  const quickActions = actions.filter((action) =>
    action.kind === "quick_action" &&
    (!action.target_section_ids?.length ||
      (selectedSectionId ? action.target_section_ids.includes(selectedSectionId) : false))
  );

  return (
    <Card className="rounded-xl border-slate-200 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
            Agent Actions
          </p>
          <h3 className="mt-1 text-base font-semibold text-slate-900">Propose a change</h3>
          <p className="mt-2 text-sm text-slate-600">
            Ask the backend workbench to generate a structured patch for this artifact.
          </p>
          <p className="mt-2 text-sm text-brand-700">
            Editing target: {selectedSectionLabel ?? "Select a patchable section below"}
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {quickActions.map((action) => (
          <button
            key={action.id}
            type="button"
            disabled={disabled || pending || !selectedSectionId}
            onClick={async () => {
              await onRequest?.({
                target_section_id: selectedSectionId,
                quick_action_id: action.id,
              });
            }}
            className="rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-brand-300 hover:text-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {action.label}
          </button>
        ))}
      </div>
      <div className="mt-4 space-y-3">
        <Textarea
          value={instruction}
          onChange={(event) => setInstruction(event.target.value)}
          rows={4}
          placeholder={
            selectedSectionLabel
              ? `Example: make ${selectedSectionLabel.toLowerCase()} more technical and less polished.`
              : "Select a patchable section, then describe the change you want."
          }
          disabled={disabled || pending}
        />
        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        ) : null}
        <div className="flex justify-end">
          <Button
            variant="primary"
            disabled={disabled || pending || !selectedSectionId || !instruction.trim()}
            onClick={async () => {
              const nextInstruction = instruction.trim();
              if (!nextInstruction) return;
              const succeeded = await onRequest?.({
                target_section_id: selectedSectionId,
                instruction: nextInstruction,
              });
              if (succeeded !== false) {
                setInstruction("");
              }
            }}
          >
            {pending ? "Generating..." : "Ask agent"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
